import { z } from 'zod/v4';
import { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE } from '../constants.js';

export const ListQuestionCommentsParamsSchema = z.object({
  questionId: z.string().min(1),
  requestId: z.string().min(1),
});

export type ListQuestionCommentsParams = z.infer<
  typeof ListQuestionCommentsParamsSchema
>;

/**
 * Only `page` and `count` — the legacy route spread `SchemaGenerator.paginationParams`
 * and nothing else, and its handler wired no sort or filter. Adding either here
 * would advertise a capability the query does not have.
 *
 * The 500 cap and the 15 default are the legacy generator's.
 */
export const ListQuestionCommentsQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(0)
    .max(MAX_PAGE_SIZE)
    .default(DEFAULT_PAGE_SIZE),
});

export type ListQuestionCommentsQuery = z.infer<
  typeof ListQuestionCommentsQuerySchema
>;

/**
 * One comment on a questionnaire question.
 *
 * **The author is `updatedBy*`, not `createdBy*`.** Both frontends map
 * `commentedByUserName` / `commentedByCompanyName` off the `updatedBy` columns —
 * the legacy POST writes the full audit block, `updated_by_*` included, on
 * insert. Reading `createdByUserName` instead would blank every author label:
 * `created_by_user_name` is not in the legacy projection and is never written
 * (the legacy `auditInsertDBColumns()` omits it), so the column is null on every
 * row that exists.
 *
 * `requestId` is deliberately **absent**. The legacy SELECT does not project it
 * even though it filters on it, and the legacy integration test pins that
 * absence.
 *
 * `looseObject` because the legacy response schema was
 * `additionalProperties: true`, so the remaining audit columns
 * (`createdByUserId`, `createdByCompanyId`, the api-key pair, the impersonate
 * pair) ride out to the wire untouched. Date columns are left undeclared so they
 * pass through as `Date` and serialise to ISO strings — the convention the rest
 * of this module's schemas set.
 *
 * Two fields frontend-v2's `RawComment` used to declare are **not** here and
 * never were on the wire: `repliedCommentId` and `edited` have no columns on
 * `supplier_questionnaire_service_supplier_questionnaire_comments`, and
 * `git log --all -S"replied_comment_id"` finds no commit in this repository's
 * history. The pre-migration route would have rejected them outright — its body
 * schema was `additionalProperties: false` over
 * `{questionId, requestId, questionComment}`.
 *
 * The old dashboard *does* render a reply thread from `repliedCommentId`, so
 * this reads like a feature lost in the migration. It is not: the Reply trigger
 * is wrapped in `{false && …}` and has been since that file's first commit, and
 * the id never leaves the browser regardless. Replies would need a column
 * first — CAPA is the working reference, on its own unrelated table.
 *
 * **This schema is the frontends' type for these rows** — it is imported by
 * `frontend-v2/src/api/questionnaires/types.ts` rather than re-declared there,
 * so a field the FE reads must be declared here or it infers as `unknown`. The
 * prose "field reads in the frontends" list this docblock used to carry was that
 * contract kept where no compiler could check it; the declarations below replace
 * it — including the two phantom fields above, which the compiler can now keep
 * out.
 *
 * `createdAt` / `updatedAt` are `z.string()` because `listQuestionCommentsDb`
 * ISO-normalises them before the serialiser sees them.
 */
export const QuestionCommentRowSchema = z.looseObject({
  id: z.string(),
  // `NOT NULL` on the comments table and always selected, so it is declared
  // required rather than `.nullable().optional()`. `questionComment` below is a
  // genuinely nullable `text` column and stays that way.
  questionId: z.string(),
  questionComment: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
  updatedByUserId: z.string().nullable().optional(),
  updatedByUserName: z.string().nullable().optional(),
  updatedByCompanyId: z.string().nullable().optional(),
  updatedByCompanyName: z.string().nullable().optional(),
});

export type QuestionCommentRow = z.infer<typeof QuestionCommentRowSchema>;

/**
 * The rows of the question comments list.
 *
 * Just the array — the `{metadata, data}` envelope around it comes from
 * `buildResponseSchemas({ 200: { type: 'data', schema, paginated: true } })` on the
 * route, the same builder every other paginated route on the platform uses. Nothing
 * here describes the envelope, so there is one definition of it rather than six.
 */
export const ListQuestionCommentsResponseSchema = z.array(
  QuestionCommentRowSchema,
);

export type ListQuestionCommentsResponse = z.infer<
  typeof ListQuestionCommentsResponseSchema
>;
